import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  ProgressFooter,
  ProgressFooterActions,
  ProgressFooterSaveNotification,
} from "../get-compliant/ProgressFooter";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";

import { Routes } from "../../root/routes";
import {
  UseDataRetentionModule,
  useDataRetentionModule,
} from "../../components/organisms/data-retention/DataRetentionModule";
import {
  UseEditPrivacyCenter,
  useEditPrivacyCenter,
} from "../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterNotice } from "../../hooks/usePrivacyCenterNotice";
import { RetentionPolicyEditor } from "../stay-compliant/privacy-center/us-state-notices/RetentionPolicyEditor";
import { PageLoading } from "../../../common/components/Loading";
import { Breadcrumbs } from "@truevault/aquilo";
import { PrivacyCenterNoticeDto } from "../../../common/service/server/dto/PrivacyCenterDto";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../stay-compliant/PrivacyNoticeChecklistItems";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { DevInstructionsFinishedDataMapOnboarding } from "./DataMapDashboardPage";
import { CheckIcon, InformationCircleIcon } from "@heroicons/react/20/solid";
import {
  BUILTIN_STEPS,
  CUSTOM_STEP_KEY,
  INTRO_STEP_KEY,
  retentionProgress,
  TOTAL_STEPS,
} from "../../components/organisms/data-retention/retentionPolicySteps";
import { DataRetentionPolicyDetailCategory } from "../../../common/service/server/dto/DataRetentionPolicyDto";

const DONE_LABEL = "Done with Data Retention";

export function StayCompliantDataRetentionPage() {
  const history = useHistory();
  const editPrivacyCenter = useEditPrivacyCenter();

  const orgId = editPrivacyCenter?.props?.org?.id;
  const privacyCenterId = editPrivacyCenter?.props?.privacyCenter?.id;

  const [draftNotice, draftNoticeRequest, refreshDraftNotice] = usePrivacyCenterNotice(
    orgId,
    privacyCenterId,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );

  const {
    answers: devInstructionsAnswers,
    setAnswer: setDevInstructionsSurveyAnswer,
    request: devInstructionsRequest,
  } = useSurvey(orgId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  // Done is the last step of the data map onboarding, so the footer only shows until that's
  // finished. Read once, so Done marking the onboarding finished can't pull the footer mid-click.
  const [showOnboardingFooter, setShowOnboardingFooter] = useState<boolean | null>(null);
  useEffect(() => {
    if (showOnboardingFooter === null && devInstructionsRequest.success) {
      setShowOnboardingFooter(
        devInstructionsAnswers[DevInstructionsFinishedDataMapOnboarding] !== "true",
      );
    }
  }, [devInstructionsAnswers, devInstructionsRequest.success, showOnboardingFooter]);

  const dataRetentionModule = useDataRetentionModule();
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);

  const [showRetentionPolicyHelpModal, setShowRetentionPolicyHelpModal] = useState(null);

  useEffect(() => {
    if (
      showRetentionPolicyHelpModal === null &&
      draftNotice &&
      editPrivacyCenter &&
      dataRetentionModule
    ) {
      const isAlreadyPublished = editPrivacyCenter?.props?.allNotices?.some(
        (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
      );
      const isFinished = isDataRetentionComplete(
        draftNotice,
        editPrivacyCenter,
        dataRetentionModule,
      );
      // Don't show the help modal if the section is finished or if user has already published their privacy policy
      setShowRetentionPolicyHelpModal(!isAlreadyPublished && !isFinished);
    }
  }, [
    draftNotice,
    editPrivacyCenter,
    dataRetentionModule,
    showRetentionPolicyHelpModal,
    setShowRetentionPolicyHelpModal,
  ]);

  if (!draftNotice || !editPrivacyCenter || !dataRetentionModule || showOnboardingFooter === null) {
    return <PageLoading />;
  }

  const latestPublishedPolicy = editPrivacyCenter?.props?.allNotices
    ?.filter((notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED")
    .sort((a, b) => b.version - a.version)[0];
  const isAlreadyPublished = latestPublishedPolicy != null;
  const isRetentionPolicySectionFinished =
    isDataRetentionComplete(draftNotice, editPrivacyCenter, dataRetentionModule) ||
    isAlreadyPublished;

  // Footer narration for the step-by-step builder: what to answer next, then a done state.
  let footerStatus: React.ReactNode = null;
  if (!draftNotice.useCustomRetentionPolicySection) {
    const { props } = dataRetentionModule;
    const progress = retentionProgress(
      props.dataRetention?.details ?? [],
      props.answers,
      props.unmappedItems.length,
    );
    const stepOf = (
      <span style={{ color: "#726D83" }}>
        (Step {progress.activeNumber} of {TOTAL_STEPS})
      </span>
    );
    if (progress.isComplete) {
      footerStatus = (
        <span className="flex items-center gap-[6px]">
          <CheckIcon className="h-[16px] w-[16px] text-green-700" />
          Retention policy complete
        </span>
      );
    } else if (progress.activeKey === INTRO_STEP_KEY) {
      footerStatus = (
        <>
          <span style={{ color: "#726D83" }}>Next:</span> save your intro statement {stepOf}
        </>
      );
    } else if (progress.activeKey === CUSTOM_STEP_KEY) {
      footerStatus = (
        <>
          <span style={{ color: "#726D83" }}>Next:</span>{" "}
          {props.unmappedItems.length > 0
            ? "give every category you collect a retention period"
            : "save your other retention purposes"}{" "}
          {stepOf}
        </>
      );
    } else {
      const title = BUILTIN_STEPS[progress.activeKey as DataRetentionPolicyDetailCategory].title;
      footerStatus = (
        <>
          <span style={{ color: "#726D83" }}>Next:</span> {title.toLowerCase()} {stepOf}
        </>
      );
    }
  }

  // Only what waits for Publish gets a notice: everything before the first publish, then only
  // "Add Your Own" text. The builder's table is filled in from the current retention settings each
  // time the published policy is shown, and intro edits rebuild the Privacy Center themselves.
  let publishNotice: string | null = null;
  if (!isAlreadyPublished) {
    publishNotice = "Your retention policy goes live when you publish your privacy policy on the";
  } else if (draftNotice.useCustomRetentionPolicySection) {
    publishNotice =
      "Changes to your retention policy text appear in your privacy policy after you click Publish on the";
  } else if (latestPublishedPolicy.useCustomRetentionPolicySection) {
    publishNotice =
      "Your privacy policy shows your own retention policy text until you click Publish on the";
  }

  return (
    <StandardPageWrapper
      requests={[
        dataRetentionModule.requests.dataMapRequest,
        dataRetentionModule.requests.dataRetentionRequest,
        dataRetentionModule.requests.request,
        draftNoticeRequest,
      ]}
      footer={
        showOnboardingFooter && (
          <ProgressFooter
            content={
              footerStatus ? (
                // .progress-footer-content sets 18px, so the size has to be inline to win.
                <span className="tw-boundary" style={{ fontSize: 14 }}>
                  {footerStatus}
                </span>
              ) : (
                <ProgressFooterSaveNotification request={dataRetentionModule.updateRequest} />
              )
            }
            actions={
              <ProgressFooterActions
                nextContent={DONE_LABEL}
                nextOnClick={async () => {
                  setIsSavingDefaults(true);
                  try {
                    await dataRetentionModule.saveDefaultDescriptions();

                    // Because this is the last step in the data map onboarding, also mark the data map onboarding as complete (if it's not already)
                    if (!devInstructionsAnswers[DevInstructionsFinishedDataMapOnboarding]) {
                      await setDevInstructionsSurveyAnswer(
                        DevInstructionsFinishedDataMapOnboarding,
                        "true",
                      );
                    }
                  } finally {
                    setIsSavingDefaults(false);
                  }
                  history.push(Routes.dataMap.dashboard);
                }}
                nextLoading={isSavingDefaults}
                nextDisabled={!isRetentionPolicySectionFinished || isSavingDefaults}
                nextDisabledTooltip={
                  draftNotice.useCustomRetentionPolicySection
                    ? "Add a custom retention policy"
                    : "Save every step to finish your retention policy"
                }
              />
            }
          />
        )
      }
      fullWidth
    >
      <RetentionPolicyEditor
        header={
          <>
            <div style={{ marginBottom: "24px" }}>
              <Breadcrumbs
                hideHomeIcon={true}
                title="Data Retention"
                source="Data Map"
                sourceRoute={Routes.dataMap.dashboard}
              />
            </div>
            {publishNotice && (
              <div
                className="mb-[16px] flex items-start gap-[8px] rounded-[6px] bg-primary-50 px-[12px] py-[10px] text-[14px] leading-[20px] text-neutral-700"
                data-testid="retention-publish-notice"
              >
                <InformationCircleIcon className="mt-[1px] h-[18px] w-[18px] flex-shrink-0 text-primary-600" />
                <span>
                  {publishNotice}{" "}
                  <Link
                    to={Routes.privacyCenter.privacyPolicy}
                    className="font-medium text-primary-600 underline"
                  >
                    Policies &amp; Notices
                  </Link>{" "}
                  page.
                </span>
              </div>
            )}
          </>
        }
        doneLabel={showOnboardingFooter ? DONE_LABEL : undefined}
        draftNotice={draftNotice}
        refreshNotice={refreshDraftNotice}
        organizationId={orgId}
        privacyCenterId={privacyCenterId}
        dataRetentionModule={dataRetentionModule}
        showHelpModal={false}
        setShowHelpModal={() => {}}
      />
    </StandardPageWrapper>
  );
}

export const isDataRetentionComplete = (
  draftNotice: PrivacyCenterNoticeDto,
  editPrivacyCenter: UseEditPrivacyCenter,
  dataRetentionModule: UseDataRetentionModule,
): boolean => {
  const customRetentionPolicySection = draftNotice.sections.find(
    (s) => s.anchor === "data-retention-policy",
  );
  if (draftNotice.useCustomRetentionPolicySection) {
    return customRetentionPolicySection?.content.length > 2;
  }
  // The footer's rule, read from the saved details, so Done can't lag the footer during a refetch.
  const { dataRetention, answers, unmappedItems } = dataRetentionModule.props;
  return (
    Boolean(dataRetention) &&
    retentionProgress(dataRetention.details ?? [], answers, unmappedItems.length).isComplete
  );
};
